import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/base/Button';
import Modal from '@/components/base/Modal';
import Skeleton from '@/components/base/Skeleton';
import { formatCurrency } from '@/lib/format';
import { systemSettings } from '@/mocks/settings';
import { fetchOutstanding, requestSettlement } from '@/services/financeService';
import type { SettlementRequest } from '@/types';

interface RequestSettlementModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (settlement: SettlementRequest) => void;
}

export default function RequestSettlementModal({ open, onClose, onSuccess }: RequestSettlementModalProps) {
  const { t } = useTranslation();

  const [loadingAmount, setLoadingAmount] = useState(false);
  const [amount, setAmount] = useState<number | null>(null);
  const [currency, setCurrency] = useState(systemSettings.currency);
  const [eligibilityError, setEligibilityError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setAmount(null);
    setEligibilityError(null);
    setSubmitError(null);
    setLoadingAmount(true);
    fetchOutstanding()
      .then((data) => {
        setAmount(data.total);
        setCurrency(data.currency);
        if (data.total <= 0) setEligibilityError(t('finance.req.noAmount'));
      })
      .catch(() => setEligibilityError(t('common.errorText')))
      .finally(() => setLoadingAmount(false));
  }, [open, t]);

  const confirmDisabled =
    loadingAmount || submitting || amount === null || amount <= 0 || Boolean(eligibilityError);

  const handleConfirm = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const settlement = await requestSettlement();
      onSuccess(settlement);
    } catch {
      // Keep the modal open with the amount preserved and show the failure reason.
      setSubmitError(t('finance.req.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={submitting ? () => undefined : onClose}
      size="sm"
      fullScreen
      title={t('finance.req.title')}
      footer={
        <>
          <Button variant="outline" size="md" onClick={onClose} disabled={submitting}>
            {t('finance.req.cancel')}
          </Button>
          <Button
            variant="primary"
            size="md"
            icon="ri-send-plane-line"
            loading={submitting}
            disabled={confirmDisabled}
            onClick={handleConfirm}
          >
            {t('finance.req.confirm')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="rounded-lg border border-background-200 bg-background-100/70 px-4 py-4">
          <p className="text-sm text-foreground-500">{t('finance.req.amountLabel')}</p>
          {loadingAmount ? (
            <div className="mt-2 space-y-2">
              <Skeleton className="h-8 w-40" />
              <p className="text-xs text-foreground-400">{t('finance.req.loading')}</p>
            </div>
          ) : (
            <p className="mt-1.5 text-3xl font-bold text-foreground-950">
              {amount === null ? '—' : formatCurrency(amount, currency)}
            </p>
          )}
          <p className="mt-2 flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
            <i className="ri-lock-2-line mt-0.5 text-base text-foreground-400" aria-hidden="true" />
            {t('finance.req.readonly')}
          </p>
        </div>

        <p className="flex items-start gap-2 text-sm leading-relaxed text-foreground-600">
          <i className="ri-information-line mt-0.5 text-base text-foreground-400" aria-hidden="true" />
          {t('finance.req.note')}
        </p>

        {eligibilityError ? (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-xs leading-relaxed text-amber-800">
            {eligibilityError}
          </p>
        ) : null}

        {submitError ? (
          <p className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-xs leading-relaxed text-rose-700">
            {submitError}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}